import { useState } from "react";
import { api, ZEFFY } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, Globe2, Megaphone, Network, Briefcase, Compass } from "lucide-react";

const INTERESTS = [
  { id: "marketplace", label: "Marketplace Visibility", icon: Globe2 },
  { id: "media", label: "Media Exposure", icon: Megaphone },
  { id: "workforce", label: "Workforce Alignment", icon: Network },
  { id: "national", label: "National Visibility", icon: Compass },
  { id: "thought", label: "Thought Leadership", icon: Briefcase },
  { id: "custom", label: "Custom Partnership", icon: ArrowRight },
];

export default function StrategicPartnership() {
  const [form, setForm] = useState({
    full_name: "", organization: "", title: "", email: "", phone: "",
    interest_areas: [], message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id) => {
    const has = form.interest_areas.includes(id);
    setForm({
      ...form,
      interest_areas: has
        ? form.interest_areas.filter((x) => x !== id)
        : [...form.interest_areas, id],
    });
  };

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partnership/strategic", form);
      toast.success("Partnership request received. Our team will reach out within 2 business days.");
      setForm({ ...form, full_name: "", title: "", email: "", phone: "", message: "", interest_areas: [] });
    } catch {
      toast.error("Could not submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="partnership"
      data-testid="strategic-partnership-section"
      className="relative py-24 md:py-32 scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="text-overline text-aawta-pink mb-4">STRATEGIC PARTNERSHIP CENTER</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] font-light text-white tracking-tight leading-[1.05]">
            For partners who think<br /> <em className="italic text-aawta-pink">in years, not nights.</em>
          </h2>
          <p className="text-white/70 mt-6 leading-relaxed">
            Year-round strategic partners co-design programs, scholarships, summits, and media
            campaigns with AAWTA. Tell us where you want impact and we&rsquo;ll architect it together.
          </p>

          <a
            href={ZEFFY.partnership}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="partnership-zeffy-btn"
            className="btn-blue mt-8"
          >
            START A PARTNERSHIP COMMITMENT <ArrowRight size={14} />
          </a>
        </div>

        <form onSubmit={submit} data-testid="partnership-form" className="md:col-span-7 glass-panel p-8 md:p-10 space-y-6">
          <div>
            <p className="text-overline text-aawta-pink">REQUEST A CONVERSATION</p>
            <h4 className="font-display text-2xl md:text-3xl text-white mt-2">Tell us where you want impact.</h4>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {INTERESTS.map((it) => {
              const Icon = it.icon;
              const active = form.interest_areas.includes(it.id);
              return (
                <button
                  type="button"
                  key={it.id}
                  data-testid={`partnership-interest-${it.id}`}
                  onClick={() => toggle(it.id)}
                  className={`text-left p-3 border transition-all flex items-start gap-2 ${
                    active ? "border-aawta-pink bg-aawta-pink/10 text-white" : "border-white/15 text-white/70 hover:border-white/40"
                  }`}
                >
                  <Icon size={16} className={active ? "text-aawta-pink" : "text-white/50"} />
                  <span className="text-xs leading-tight">{it.label}</span>
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-6 pt-2">
            <div>
              <label className="label-luxe">Name *</label>
              <input data-testid="prt-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
            </div>
            <div>
              <label className="label-luxe">Title</label>
              <input data-testid="prt-title" className="input-luxe" value={form.title} onChange={handle("title")} />
            </div>
            <div>
              <label className="label-luxe">Organization *</label>
              <input data-testid="prt-org" required className="input-luxe" value={form.organization} onChange={handle("organization")} />
            </div>
            <div>
              <label className="label-luxe">Email *</label>
              <input data-testid="prt-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
            </div>
          </div>
          <div>
            <label className="label-luxe">Phone</label>
            <input data-testid="prt-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
          </div>
          <div>
            <label className="label-luxe">Vision / Message</label>
            <textarea data-testid="prt-message" rows={4} className="input-luxe resize-none" value={form.message} onChange={handle("message")} />
          </div>
          <button type="submit" data-testid="prt-submit" disabled={submitting} className="btn-pink w-full disabled:opacity-50">
            {submitting ? "SENDING..." : "SUBMIT PARTNERSHIP REQUEST"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </section>
  );
}
